import { useState } from "react";

export const usePaymentFileValidation = () => {
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string>("");
  const [paymentCount, setPaymentCount] = useState<number>(0);
  const [redriveOK, setRedriveOK] = useState<boolean>(false);

  const handleFileChange = (files: File[]) => {
    setFile(files[0]);
    setRedriveOK(false);
  };

  const validateLineFormat = (line: string) => {
    // PaymentIds are alphanumeric (plus _ - #), one per line, no commas
    const trimmed = line.trim();
    return trimmed.length > 0 && /^[a-zA-Z0-9_#-]+$/.test(trimmed);
  };

  const validateDuplicates = (lines: string[]) => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const line of lines) {
      if (seen.has(line)) duplicates.add(line);
      seen.add(line);
    }
    if (duplicates.size > 0) {
      setError(
        `${duplicates.size} duplicate payment ID${
          duplicates.size === 1 ? "" : "s"
        } found. Please remove duplicates before continuing.`
      );
      return false;
    }
    return true;
  };

  const validateRedrive = () => {
    if (!file) {
      setError("Please upload a CSV file");
      return;
    }
    if (file.type !== "text/csv") {
      setError("File type must be a CSV. Please upload another");
      return;
    }
    if (file.size === 0) {
      setError("File is empty. Please choose another.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target?.result) return;
      const lines = e.target.result
        .toString()
        .split("\n")
        .filter((l) => l.trim() !== "");
      setPaymentCount(lines.length);

      let isOk = true;
      for (const line of lines) {
        if (!validateLineFormat(line)) {
          setError(
            "Invalid line format. CSV file should only contain payment IDs, one per line."
          );
          isOk = false;
          break;
        }
      }
      if (isOk && !validateDuplicates(lines)) isOk = false;

      setRedriveOK(isOk);
      if (isOk) setError("");
    };
    reader.readAsText(file);
  };

  const removeDuplicates = () => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target?.result) return;
      const lines = e.target.result
        .toString()
        .split("\n")
        .filter((l) => l.trim() !== "");
      const unique = [...new Set(lines)];
      const blob = new Blob([unique.join("\n")], { type: "text/csv" });
      setFile(new File([blob], file.name, { type: "text/csv" }));
      setRedriveOK(false);
      setError("");
    };
    reader.readAsText(file);
  };

  return {
    file,
    paymentCount,
    error,
    redriveOK,
    handleFileChange,
    validateRedrive,
    setError,
    removeDuplicates,
  };
};
